import React, { useEffect, useRef, useState } from 'react';
import { Form, Formik } from 'formik';
import { useParams } from 'react-router-dom';

import AgModalWindow from '@/components/common/ModalWindow/AgModalWindow';
import Textarea from '@/components/common/Textarea/Textarea';
import ActionButtonsWrapper from '@/components/common/ModalWindow/ActionButtonsWrapper/ActionButtonsWrapper';
import Button, { ButtonStyleType } from '@/components/common/Button/Button';
import ModalFormDropdown from '@/components/business/ModalForm/ModalFormDropdown/ModalFormDropdown';
import TopLabel from '@/components/common/TopLabel/TopLabel';
import { Fields, validationSchema } from './validationSchema';
import FieldCheckboxList from './FieldCheckboxList';
import { useGetReasonCodes } from '@/pages/SystemAdmin/ReasonCodes/hooks/useGetReasonCodes';
import { useGetVerifiableFields } from '../../hooks/useGetVerifiableFields';
import { useVerifyFields } from '../../hooks/useVerifyFields';
import { useRemoveVerifiableField } from '../../hooks/useRemoveVerifiableField';
import { CONTRACT_IQ_TYPE } from '@/config/reasonTypes.config';
import { EntityTypeEnum } from '@/graphql/contracts/_generated_/contracts';
import { COMMENT_CHARS_ALLOWED_LENGTH } from '@/config/common.config';
import { useGetUserRole } from '@/hooks/identity/useGetUserRole';
import { UserRole } from '@/config/roles.config';
import AddVerifiableFieldModal from '../AddVerifiableFieldModal/AddVerifiableFieldModal';
import {
  CONTRACT_IQ_CONTRACTS_AND_BILLING_REASON_CODE,
} from './contractIqVerification.config';
import {
  toggleAllFieldSelections,
  toggleFieldSelection,
  toggleMissingAgreementSelection,
} from './fieldSelection';

import styles from './contract-iq-verify-modal.module.scss';

interface IContractIqVerifyModalProps {
  onClose: () => void;
  onSuccess: () => void;
}

export default function ContractIqVerifyModal({
  onClose,
  onSuccess,
}: IContractIqVerifyModalProps) {
  const { contractId }: { contractId: UUID } = useParams();
  const { getReasonCodes, reasonCodes: allReasonCodes, isLoading: isLoadingReasonCodes } = useGetReasonCodes();
  const { getVerifiableFields, verifiableFields, isLoading: isLoadingFields } = useGetVerifiableFields();
  const { verifyFields, isLoading: isSubmitting } = useVerifyFields();
  const { removeVerifiableField, isLoading: isRemoving } = useRemoveVerifiableField();
  const { getUserRole } = useGetUserRole();
  const [isFullAccess, setIsFullAccess] = useState(false);
  const [showAddFieldModal, setShowAddFieldModal] = useState(false);
  const selectedReasonCodeRef = useRef('');

  useEffect(() => {
    getReasonCodes(CONTRACT_IQ_TYPE);
  }, [getReasonCodes]);

  useEffect(() => {
    const checkRole = async () => {
      const role = await getUserRole();
      setIsFullAccess(role === UserRole.FULL_ACCESS);
    };
    checkRole();
  }, [getUserRole]);

  // Filter reason codes client-side to only show ContractIQ type
  const reasonCodes = allReasonCodes.filter(rc => rc.reasonType?.type === CONTRACT_IQ_TYPE);

  const handleReasonCodeChange = (reasonCode: string) => {
    if (reasonCode) {
      getVerifiableFields(reasonCode, EntityTypeEnum.CONTRACT);
    }
  };

  const handleRemoveField = (field: { field: string; reasonCode: string; entityType: EntityTypeEnum }) => {
    removeVerifiableField(
      {
        field: field.field,
        reasonCode: field.reasonCode,
        entityType: field.entityType,
      },
      () => {
        if (selectedReasonCodeRef.current) {
          getVerifiableFields(selectedReasonCodeRef.current, EntityTypeEnum.CONTRACT);
        }
      }
    );
  };

  return (
    <>
      <AgModalWindow
        isOpen={true}
        onClose={onClose}
        title="Contract IQ Verify"
        contentStyle={{ width: 550, maxHeight: '90vh', overflowY: 'auto' }}
      >
        <Formik<Fields>
          initialValues={{
            reasonCode: '',
            comment: '',
            selectedFields: [],
          }}
          validationSchema={validationSchema}
          validateOnMount={true}
          onSubmit={(values) => {
            verifyFields(
              {
                entityID: contractId,
                entityType: EntityTypeEnum.CONTRACT,
                fields: values.selectedFields,
                reasonCode: values.reasonCode,
                comment: values.comment.trim() || undefined,
              },
              () => {
                onSuccess();
                onClose();
              }
            );
          }}
        >
          {({ values, errors, touched, setFieldValue, setFieldTouched, isValid }) => (
            <Form>
              <ModalFormDropdown
                id="reasonCode"
                labelTitle="Reason Name"
                requiredIcon={true}
                optionsData={reasonCodes}
                getOptionLabel={(option) => option.name}
                getOptionValue={(option) => option.code}
                isLoading={isLoadingReasonCodes}
                placeholder="Select reason..."
                selectedOption={allReasonCodes.find(rc => rc.code === values.reasonCode) ?? null}
                handleChange={(newValue) => {
                  const code = newValue?.code ?? '';
                  setFieldValue('reasonCode', code);
                  setFieldValue('selectedFields', []);
                  selectedReasonCodeRef.current = code;
                  handleReasonCodeChange(code);
                }}
                validationMessage={
                  touched.reasonCode && !values.reasonCode && errors.reasonCode
                    ? errors.reasonCode
                    : undefined
                }
              />

              <div className={styles['notes-section']}>
                <TopLabel
                  id="comment"
                  label="Notes (optional)"
                />
                <Textarea
                  value={values.comment}
                  placeholder="Add verification notes for all selected fields..."
                  onChange={(e) => {
                    setFieldValue('comment', e.target.value);
                    setFieldTouched('comment', true, false);
                  }}
                  maxLength={COMMENT_CHARS_ALLOWED_LENGTH}
                />
                {touched.comment && errors.comment && (
                  <span className={styles['error-text']}>{errors.comment}</span>
                )}
              </div>

              {isFullAccess && (
                <div className={styles['add-field-link-wrapper']}>
                  <button
                    type="button"
                    className={styles['add-field-link']}
                    onClick={() => setShowAddFieldModal(true)}
                  >
                    Add New Verifiable Field
                  </button>
                </div>
              )}

              {values.reasonCode && (
                <FieldCheckboxList
                  fields={verifiableFields}
                  selectedFields={values.selectedFields}
                  isLoading={isLoadingFields}
                  isFullAccess={isFullAccess}
                  showMissingAgreement={
                    values.reasonCode === CONTRACT_IQ_CONTRACTS_AND_BILLING_REASON_CODE
                  }
                  onRemoveField={handleRemoveField}
                  isRemoving={isRemoving}
                  onToggleField={(field) => {
                    setFieldValue(
                      'selectedFields',
                      toggleFieldSelection(values.selectedFields, field)
                    );
                    setFieldTouched('selectedFields', true, false);
                  }}
                  onToggleAll={() => {
                    const fieldNames = verifiableFields.map(vf => vf.field);
                    setFieldValue(
                      'selectedFields',
                      toggleAllFieldSelections(values.selectedFields, fieldNames)
                    );
                    setFieldTouched('selectedFields', true, false);
                  }}
                  onToggleMissingAgreement={() => {
                    setFieldValue(
                      'selectedFields',
                      toggleMissingAgreementSelection(values.selectedFields)
                    );
                    setFieldTouched('selectedFields', true, false);
                  }}
                />
              )}

              {values.reasonCode
                && (
                  verifiableFields.length > 0
                  || values.reasonCode === CONTRACT_IQ_CONTRACTS_AND_BILLING_REASON_CODE
                )
                && (
                  <div className={styles['selection-count']}>
                    {values.selectedFields.length === 0
                      ? 'No fields selected'
                      : `${values.selectedFields.length} field${values.selectedFields.length > 1 ? 's' : ''} selected`
                    }
                  </div>
                )}

              <ActionButtonsWrapper
                showDivider={false}
                leftButton={(
                  <Button
                    buttonStyleType={ButtonStyleType.SECONDARY}
                    onClick={onClose}
                  >
                    Cancel
                  </Button>
                )}
                rightButton={(
                  <Button
                    disabled={!isValid || isSubmitting}
                    type="submit"
                    buttonStyleType={ButtonStyleType.PRIMARY}
                  >
                    Submit Verification
                  </Button>
                )}
              />
            </Form>
          )}
        </Formik>
      </AgModalWindow>

      {showAddFieldModal && (
        <AddVerifiableFieldModal
          onClose={() => setShowAddFieldModal(false)}
          onSuccess={() => {
            if (selectedReasonCodeRef.current) {
              getVerifiableFields(selectedReasonCodeRef.current, EntityTypeEnum.CONTRACT);
            }
          }}
        />
      )}
    </>
  );
}
